import axios from 'axios'

/**
 * Base URL helpers.
 *
 * DOCS_API_URL  →  Spr2.Api.Documents  (file upload/download; 737 MB body limit)
 *   dev:   https://localhost:44325/
 *   prod:  https://documents-test.responsegrid.net/
 *
 * API_URL       →  Spr2.Api  (all job/status endpoints)
 *   dev:   http://localhost:51562/
 *   prod:  https://api.responsegrid.net/
 */

/** Routes to Spr2.Api.Documents — use for file upload/download endpoints. */
function docsBase (companyId) {
  return `${process.env.DOCS_API_URL}api/company/${companyId}/importexport`
}

/** Routes to Spr2.Api — use for async job endpoints. */
function base (companyId) {
  return `${process.env.API_URL}api/company/${companyId}/importexport`
}

export default {
  /**
   * Download a blank template for the given entity type.
   * 📍 Spr2.Api.Documents — routed here to avoid API Gateway binary encoding issues.
   * Returns a Blob.
   */
  downloadTemplate (companyId, entityType) {
    return axios.get(`${docsBase(companyId)}/template/${entityType}`, {
      responseType: 'blob'
    })
  },

  /**
   * Validate an uploaded Excel file without importing.
   * 📍 Spr2.Api.Documents — multipart file upload.
   * @param {string} companyId
   * @param {string} entityType
   * @param {File} file
   * @returns ImportValidationResult
   */
  validate (companyId, entityType, file) {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post(`${docsBase(companyId)}/validate/${entityType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  /**
   * Import data from an Excel file.
   * 📍 Spr2.Api.Documents — multipart file upload.
   * @param {string} companyId
   * @param {string} entityType
   * @param {File} file
   * @param {string} mode - 'upsert' | 'appendonly' | 'replace'
   * @returns ImportResult
   */
  importData (companyId, entityType, file, mode = 'upsert') {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post(`${docsBase(companyId)}/import/${entityType}?mode=${mode}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  /**
   * Download a pre-filled export for the given entity type.
   * 📍 Spr2.Api.Documents — potentially large generated .xlsx.
   * Returns a Blob.
   * @param {object} options - { includeArchived } — when true, archived records are included in the export
   */
  downloadExport (companyId, entityType, options = {}) {
    const params = {}
    if (options.includeArchived) params.includeArchived = true
    return axios.get(`${docsBase(companyId)}/export/${entityType}`, {
      params,
      responseType: 'blob'
    })
  },

  /**
   * Download a zip of all entity type exports (synchronous).
   * 📍 Spr2.Api.Documents — multi-xlsx zip.
   * Returns a Blob.
   * @param {object} options - { includeArchived } — when true, archived records are included for supporting types
   */
  downloadAllExports (companyId, options = {}) {
    const params = {}
    if (options.includeArchived) params.includeArchived = true
    return axios.get(`${docsBase(companyId)}/export/all`, {
      params,
      responseType: 'blob'
    })
  },

  /**
   * Download files for a single property as a zip (synchronous).
   * 📍 Spr2.Api.Documents — potentially large S3 file zip.
   * @param {string} companyId
   * @param {string} propertyId
   * @param {object} options - { preserveFolderStructure, includeArchived }
   */
  downloadPropertyFiles (companyId, propertyId, options = {}) {
    return axios.get(`${docsBase(companyId)}/export/property-files/${propertyId}`, {
      params: options,
      responseType: 'blob'
    })
  },

  /**
   * Get a pre-flight size estimate for the company archive (synchronous).
   * 📍 Spr2.Api — lightweight query, no file transfer.
   * @param {string} companyId
   * @param {object} options - { includeCompanyFiles, includePropertyFiles, includeEventData, includeInsuranceData }
   * Returns a CompanyArchiveEstimate.
   */
  getArchiveEstimate (companyId, options = {}) {
    return axios.get(`${base(companyId)}/export/archive/estimate`, { params: options })
  },

  // ─── Async Export Job Methods (v1.3) ────────────────────────────────────────
  // All job endpoints are JSON-only (no file payload) and stay on Spr2.Api.

  /**
   * Request a background export job.
   * 📍 Spr2.Api — JSON body; queues SQS message.
   * @param {string} companyId
   * @param {string} jobType - 'EventArchive' | 'AllPropertyFiles' | 'CompanyArchive'
   * @param {object} parameters - Job-type-specific options object
   * @returns CreateExportJobResponse { jobId, status, estimatedWaitMinutes, pollUrl }
   */
  createExportJob (companyId, jobType, parameters = {}) {
    // Map the generic 'parameters' object to the C# model's type-specific property name.
    // CreateExportJobRequest has AllPropertyFilesParameters / EventArchiveParameters /
    // CompanyArchiveParameters — not a generic 'parameters' field.
    const paramKeyMap = {
      AllPropertyFiles: 'allPropertyFilesParameters',
      EventArchive:     'eventArchiveParameters',
      CompanyArchive:   'companyArchiveParameters'
    }
    const paramKey = paramKeyMap[jobType]
    const body = (paramKey && parameters && Object.keys(parameters).length > 0)
      ? { jobType, [paramKey]: parameters }
      : { jobType }
    return axios.post(`${base(companyId)}/jobs`, body)
  },

  /**
   * Get the list of recent export jobs for the current user.
   * 📍 Spr2.Api — JSON response.
   * @param {string} companyId
   * @param {number} limit - max jobs to return (default 10)
   * @returns Array of GetExportJobStatusResponse
   */
  getExportJobs (companyId, limit = 10) {
    return axios.get(`${base(companyId)}/jobs`, { params: { limit } })
  },

  /**
   * Poll the status of a single export job.
   * 📍 Spr2.Api — JSON response (status poll).
   * @param {string} companyId
   * @param {string} jobId
   * @returns GetExportJobStatusResponse { jobId, status, downloadUrl, expiresAtUtc, ... }
   */
  getExportJobStatus (companyId, jobId) {
    return axios.get(`${base(companyId)}/jobs/${jobId}`)
  },

  /**
   * Get a redirect to the pre-signed S3 download URL for a completed job.
   * 📍 Spr2.Api — returns 302 redirect; no large file goes through the API.
   * The caller should extract the Location header and trigger a browser download.
   * @param {string} companyId
   * @param {string} jobId
   */
  getExportJobDownloadUrl (companyId, jobId) {
    // Use maxRedirects: 0 to capture the 302 Location header,
    // then trigger a browser download from that URL.
    return axios.get(`${base(companyId)}/jobs/${jobId}/download`, {
      maxRedirects: 0,
      validateStatus: status => status === 302 || status === 200
    })
  },

  /**
   * Get a count of events matching the given filters.
   * 📍 Spr2.Api — lightweight count query.
   * @param {string} companyId
   * @param {object} filters — { startDate, endDate, propertyId, categoryId }
   * @returns { count: number }
   */
  getEventsCount (companyId, filters = {}) {
    return axios.get(`${process.env.API_URL}api/company/${companyId}/events/count`, { params: filters })
  },

  /**
   * Upload an insurance policy document (PDF or image) and extract policy data using AI (Bedrock).
   * 📍 Spr2.Api — multipart file upload; returns AiPolicyExtractionResult JSON.
   *
   * IMPORTANT: This endpoint NEVER saves data. It returns a suggestion for human review.
   * The caller must POST to the standard save endpoint after the user reviews/edits the result.
   *
   * @param {string} companyId
   * @param {File} file — PDF or image, max 20 MB
   * @returns AiPolicyExtractionResult { success, errorMessage, suggestedPolicy, changeSummary,
   *                                     matchedExistingPolicyId, confidenceScore, extractedRawText }
   */
  aiExtractPolicy (companyId, file) {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post(
      `${process.env.API_URL}api/company/${companyId}/insurancepolicies/ai-extract`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
  }
}



// WEBPACK FOOTER //
// ./src/api/importExportApi.js